import { ConnectorFormValues } from "../../../../../../types"

interface UseConnectorFormStateProps {
  values: ConnectorFormValues
  setFormField: (name: string, val: any) => void
  setFieldTouched?: (field: string, touched: boolean) => void
  validateForm?: () => void
}

export const useConnectorFormState = ({
  values,
  setFormField,
  setFieldTouched,
  validateForm
}: UseConnectorFormStateProps) => {
  const handleImport = (importedData: Partial<ConnectorFormValues>) => {
    // Set all imported fields
    Object.entries(importedData).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        setFormField(key, value)
      }
    })

    // Mark required fields as touched to clear validation errors
    setTimeout(() => {
      const fieldsToTouch = [
        "name",
        "brand_id",
        "owner",
        "direction",
        "data_type"
      ]
      fieldsToTouch.forEach(field => {
        if (setFieldTouched) {
          setFieldTouched(field, true)
        }
      })

      // Trigger validation
      if (validateForm) {
        validateForm()
      }
    }, 200)
  }

  const handleExport = () => {
    const exportData = {
      name: values.name,
      type: values.type,
      direction: values.direction,
      data_type: values.data_type,
      webhook_url: values.webhook_url,
      event: values.event,
      field_mapping: values.field_mapping,
      template: values.template,
      config: values.config,
      active: values.active
    }

    const blob = new Blob([JSON.stringify(exportData, null, 2)], {
      type: "application/json"
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement("a")
    a.href = url
    a.download = `connector-${values.name || "export"}.json`
    a.click()
    URL.revokeObjectURL(url)
  }

  const handleDataTypeChange = (newDataType: string) => {
    setFormField("data_type", newDataType)

    // Update event when data type changes for outgoing connectors
    if (values.direction === "outgoing") {
      setFormField(
        "event",
        newDataType === "pending_subscription"
          ? "pending_subscription.converted"
          : `${newDataType}.created`
      )
    }

    // Reset field mapping when data type changes
    setFormField("field_mapping", [])

    // lead_score connectors always send JSON — lock content_type so it stays
    // consistent if the user previously selected URL-encoded or form-data.
    if (newDataType === "lead_score") {
      setFormField("config", {
        ...values.config,
        content_type: "application/json",
        headers: {
          ...values.config?.headers,
          "Content-Type": "application/json"
        }
      })
    }
  }

  const handleContentTypeChange = (contentType: string) => {
    setFormField("config", {
      ...values.config,
      content_type: contentType,
      headers: {
        ...values.config?.headers,
        "Content-Type": contentType
      }
    })
  }

  const handleHeadersChange = (headersJson: string) => {
    try {
      const headers = JSON.parse(headersJson)
      setFormField("config", { ...values.config, headers })
      return { success: true, error: null }
    } catch (error) {
      return { success: false, error: "Invalid JSON" }
    }
  }

  const handleTimeoutChange = (timeout: number) => {
    setFormField("config", {
      ...values.config,
      timeout
    })
  }

  const handleBasicAuthChange = (
    field: "username" | "password",
    value: string
  ) => {
    setFormField("config", {
      ...values.config,
      basic_auth: {
        ...values.config?.basic_auth,
        [field]: value
      }
    })
  }

  const copyWebhookUrl = () => {
    // For incoming connectors, use incoming_webhook_url field
    const urlToCopy = values.incoming_webhook_url || values.webhook_url
    if (urlToCopy) {
      navigator.clipboard.writeText(urlToCopy)
      return true
    }
    return false
  }

  return {
    handleImport,
    handleExport,
    handleDataTypeChange,
    handleContentTypeChange,
    handleHeadersChange,
    handleTimeoutChange,
    handleBasicAuthChange,
    copyWebhookUrl
  }
}
